import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Error patterns caused by Google Translate modifying the DOM
const GOOGLE_TRANSLATE_ERROR_PATTERNS = [
  "Failed to execute 'removeChild' on 'Node'",
  "Failed to execute 'insertBefore' on 'Node'",
  "Failed to execute 'appendChild' on 'Node'",
  'The node to be removed is not a child of this node',
  'The node before which the new node is to be inserted is not a child of this node',
  'NotFoundError',
];

let originalRemoveChild = null;
let originalInsertBefore = null;

function patchDomMethodsForGoogleTranslate() {
  if (typeof window === 'undefined') return;
  if (originalRemoveChild) return;

  originalRemoveChild = Node.prototype.removeChild;
  originalInsertBefore = Node.prototype.insertBefore;

  Node.prototype.removeChild = function (child) {
    if (child.parentNode !== this) {
      if (child.parentNode?.parentNode === this) {
        return originalRemoveChild.call(this, child.parentNode);
      }
      if (child.parentNode) {
        return originalRemoveChild.call(child.parentNode, child);
      }
      return child;
    }
    return originalRemoveChild.call(this, child);
  };

  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (referenceNode.parentNode?.parentNode === this) {
        return originalInsertBefore.call(this, newNode, referenceNode.parentNode);
      }
      return this.appendChild(newNode);
    }
    return originalInsertBefore.call(this, newNode, referenceNode);
  };
}

export const GOOGLE_TRANSLATE_LANGUAGES = [
  // English first
  { code: 'en', name: 'English', englishName: 'English', countryCode: 'US' },
  // Then alphabetically by English name
  {
    code: 'af',
    name: 'Afrikaans',
    englishName: 'Afrikaans',
    countryCode: 'ZA',
  },
  { code: 'sq', name: 'Shqip', englishName: 'Albanian', countryCode: 'AL' },
  { code: 'am', name: 'አማርኛ', englishName: 'Amharic', countryCode: 'ET' },
  { code: 'ar', name: 'العربية', englishName: 'Arabic', countryCode: 'SA' },
  { code: 'hy', name: 'Հայերեն', englishName: 'Armenian', countryCode: 'AM' },
  { code: 'as', name: 'অসমীয়া', englishName: 'Assamese', countryCode: 'IN' },
  { code: 'ay', name: 'Aymar aru', englishName: 'Aymara', countryCode: 'BO' },
  {
    code: 'az',
    name: 'Azərbaycan',
    englishName: 'Azerbaijani',
    countryCode: 'AZ',
  },
  { code: 'bm', name: 'Bamanankan', englishName: 'Bambara', countryCode: 'ML' },
  { code: 'eu', name: 'Euskara', englishName: 'Basque', countryCode: 'ES' },
  {
    code: 'be',
    name: 'Беларуская',
    englishName: 'Belarusian',
    countryCode: 'BY',
  },
  { code: 'bn', name: 'বাংলা', englishName: 'Bengali', countryCode: 'BD' },
  { code: 'bho', name: 'भোজपुरी', englishName: 'Bhojpuri', countryCode: 'IN' },
  { code: 'bs', name: 'Bosanski', englishName: 'Bosnian', countryCode: 'BA' },
  {
    code: 'bg',
    name: 'Български',
    englishName: 'Bulgarian',
    countryCode: 'BG',
  },
  { code: 'my', name: 'မြန်မာ', englishName: 'Burmese', countryCode: 'MM' },
  { code: 'ca', name: 'Català', englishName: 'Catalan', countryCode: 'ES' },
  { code: 'ceb', name: 'Cebuano', englishName: 'Cebuano', countryCode: 'PH' },
  {
    code: 'zh-CN',
    name: '简体中文',
    englishName: 'Chinese (Simplified)',
    countryCode: 'CN',
  },
  {
    code: 'zh-TW',
    name: '繁體中文',
    englishName: 'Chinese (Traditional)',
    countryCode: 'TW',
  },
  { code: 'co', name: 'Corsu', englishName: 'Corsican', countryCode: 'FR' },
  { code: 'hr', name: 'Hrvatski', englishName: 'Croatian', countryCode: 'HR' },
  { code: 'cs', name: 'Čeština', englishName: 'Czech', countryCode: 'CZ' },
  { code: 'da', name: 'Dansk', englishName: 'Danish', countryCode: 'DK' },
  { code: 'dv', name: 'ދިވެހި', englishName: 'Dhivehi', countryCode: 'MV' },
  { code: 'doi', name: 'डोगरी', englishName: 'Dogri', countryCode: 'IN' },
  { code: 'nl', name: 'Nederlands', englishName: 'Dutch', countryCode: 'NL' },
  {
    code: 'eo',
    name: 'Esperanto',
    englishName: 'Esperanto',
    countryCode: 'EU',
  },
  { code: 'et', name: 'Eesti', englishName: 'Estonian', countryCode: 'EE' },
  { code: 'ee', name: 'Eʋegbe', englishName: 'Ewe', countryCode: 'GH' },
  { code: 'fil', name: 'Filipino', englishName: 'Filipino', countryCode: 'PH' },
  { code: 'fi', name: 'Suomi', englishName: 'Finnish', countryCode: 'FI' },
  { code: 'fr', name: 'Français', englishName: 'French', countryCode: 'FR' },
  { code: 'fy', name: 'Frysk', englishName: 'Frisian', countryCode: 'NL' },
  { code: 'gl', name: 'Galego', englishName: 'Galician', countryCode: 'ES' },
  { code: 'ka', name: 'ქართული', englishName: 'Georgian', countryCode: 'GE' },
  { code: 'de', name: 'Deutsch', englishName: 'German', countryCode: 'DE' },
  { code: 'el', name: 'Ελληνικά', englishName: 'Greek', countryCode: 'GR' },
  { code: 'gn', name: 'Guarani', englishName: 'Guarani', countryCode: 'PY' },
  { code: 'gu', name: 'ગુજરાતી', englishName: 'Gujarati', countryCode: 'IN' },
  {
    code: 'ht',
    name: 'Kreyòl ayisyen',
    englishName: 'Haitian Creole',
    countryCode: 'HT',
  },
  { code: 'ha', name: 'Hausa', englishName: 'Hausa', countryCode: 'NG' },
  {
    code: 'haw',
    name: 'ʻŌlelo Hawaiʻi',
    englishName: 'Hawaiian',
    countryCode: 'US',
  },
  { code: 'he', name: 'עברית', englishName: 'Hebrew', countryCode: 'IL' },
  { code: 'hi', name: 'हिन्दी', englishName: 'Hindi', countryCode: 'IN' },
  { code: 'hmn', name: 'Hmoob', englishName: 'Hmong', countryCode: 'LA' },
  { code: 'hu', name: 'Magyar', englishName: 'Hungarian', countryCode: 'HU' },
  { code: 'is', name: 'Íslenska', englishName: 'Icelandic', countryCode: 'IS' },
  { code: 'ig', name: 'Igbo', englishName: 'Igbo', countryCode: 'NG' },
  { code: 'ilo', name: 'Ilokano', englishName: 'Ilocano', countryCode: 'PH' },
  {
    code: 'id',
    name: 'Bahasa Indonesia',
    englishName: 'Indonesian',
    countryCode: 'ID',
  },
  { code: 'ga', name: 'Gaeilge', englishName: 'Irish', countryCode: 'IE' },
  { code: 'it', name: 'Italiano', englishName: 'Italian', countryCode: 'IT' },
  { code: 'ja', name: '日本語', englishName: 'Japanese', countryCode: 'JP' },
  { code: 'jv', name: 'Basa Jawa', englishName: 'Javanese', countryCode: 'ID' },
  { code: 'kn', name: 'ಕನ್ನಡ', englishName: 'Kannada', countryCode: 'IN' },
  { code: 'kk', name: 'Қазақ', englishName: 'Kazakh', countryCode: 'KZ' },
  { code: 'km', name: 'ខ្មែរ', englishName: 'Khmer', countryCode: 'KH' },
  {
    code: 'rw',
    name: 'Kinyarwanda',
    englishName: 'Kinyarwanda',
    countryCode: 'RW',
  },
  { code: 'gom', name: 'कोंकणी', englishName: 'Konkani', countryCode: 'IN' },
  { code: 'ko', name: '한국어', englishName: 'Korean', countryCode: 'KR' },
  { code: 'kri', name: 'Krio', englishName: 'Krio', countryCode: 'SL' },
  {
    code: 'ku',
    name: 'Kurdî',
    englishName: 'Kurdish (Kurmanji)',
    countryCode: 'TR',
  },
  {
    code: 'ckb',
    name: 'کوردی',
    englishName: 'Kurdish (Sorani)',
    countryCode: 'IQ',
  },
  { code: 'ky', name: 'Кыргызча', englishName: 'Kyrgyz', countryCode: 'KG' },
  { code: 'lo', name: 'ລາວ', englishName: 'Lao', countryCode: 'LA' },
  { code: 'la', name: 'Latina', englishName: 'Latin', countryCode: 'VA' },
  { code: 'lv', name: 'Latviešu', englishName: 'Latvian', countryCode: 'LV' },
  { code: 'ln', name: 'Lingála', englishName: 'Lingala', countryCode: 'CD' },
  {
    code: 'lt',
    name: 'Lietuvių',
    englishName: 'Lithuanian',
    countryCode: 'LT',
  },
  { code: 'lg', name: 'Luganda', englishName: 'Luganda', countryCode: 'UG' },
  {
    code: 'lb',
    name: 'Lëtzebuergesch',
    englishName: 'Luxembourgish',
    countryCode: 'LU',
  },
  {
    code: 'mk',
    name: 'Македонски',
    englishName: 'Macedonian',
    countryCode: 'MK',
  },
  { code: 'mai', name: 'मैथिली', englishName: 'Maithili', countryCode: 'IN' },
  { code: 'mg', name: 'Malagasy', englishName: 'Malagasy', countryCode: 'MG' },
  {
    code: 'ms',
    name: 'Bahasa Melayu',
    englishName: 'Malay',
    countryCode: 'MY',
  },
  { code: 'ml', name: 'മലയാളം', englishName: 'Malayalam', countryCode: 'IN' },
  { code: 'mt', name: 'Malti', englishName: 'Maltese', countryCode: 'MT' },
  { code: 'mi', name: 'Te Reo Māori', englishName: 'Maori', countryCode: 'NZ' },
  { code: 'mr', name: 'मराठी', englishName: 'Marathi', countryCode: 'IN' },
  {
    code: 'mni-Mtei',
    name: 'ꯃꯤꯇꯩꯂꯣꯟ',
    englishName: 'Meiteilon (Manipuri)',
    countryCode: 'IN',
  },
  { code: 'lus', name: 'Mizo ṭawng', englishName: 'Mizo', countryCode: 'IN' },
  { code: 'mn', name: 'Монгол', englishName: 'Mongolian', countryCode: 'MN' },
  { code: 'ne', name: 'नेपाली', englishName: 'Nepali', countryCode: 'NP' },
  { code: 'no', name: 'Norsk', englishName: 'Norwegian', countryCode: 'NO' },
  {
    code: 'ny',
    name: 'Chichewa',
    englishName: 'Nyanja (Chichewa)',
    countryCode: 'MW',
  },
  { code: 'or', name: 'ଓଡ଼ିଆ', englishName: 'Odia (Oriya)', countryCode: 'IN' },
  { code: 'om', name: 'Oromoo', englishName: 'Oromo', countryCode: 'ET' },
  { code: 'ps', name: 'پښتو', englishName: 'Pashto', countryCode: 'AF' },
  { code: 'fa', name: 'فارسی', englishName: 'Persian', countryCode: 'IR' },
  { code: 'pl', name: 'Polski', englishName: 'Polish', countryCode: 'PL' },
  {
    code: 'pt',
    name: 'Português',
    englishName: 'Portuguese',
    countryCode: 'BR',
  },
  { code: 'pa', name: 'ਪੰਜਾਬੀ', englishName: 'Punjabi', countryCode: 'IN' },
  { code: 'qu', name: 'Runasimi', englishName: 'Quechua', countryCode: 'PE' },
  { code: 'ro', name: 'Română', englishName: 'Romanian', countryCode: 'RO' },
  { code: 'ru', name: 'Русский', englishName: 'Russian', countryCode: 'RU' },
  {
    code: 'sm',
    name: 'Gagana Samoa',
    englishName: 'Samoan',
    countryCode: 'WS',
  },
  { code: 'sa', name: 'संस्कृत', englishName: 'Sanskrit', countryCode: 'IN' },
  {
    code: 'gd',
    name: 'Gàidhlig',
    englishName: 'Scots Gaelic',
    countryCode: 'GB',
  },
  { code: 'nso', name: 'Sepedi', englishName: 'Sepedi', countryCode: 'ZA' },
  { code: 'sr', name: 'Српски', englishName: 'Serbian', countryCode: 'RS' },
  { code: 'st', name: 'Sesotho', englishName: 'Sesotho', countryCode: 'LS' },
  { code: 'sn', name: 'ChiShona', englishName: 'Shona', countryCode: 'ZW' },
  { code: 'sd', name: 'سنڌي', englishName: 'Sindhi', countryCode: 'PK' },
  { code: 'si', name: 'සිංහල', englishName: 'Sinhala', countryCode: 'LK' },
  { code: 'sk', name: 'Slovenčina', englishName: 'Slovak', countryCode: 'SK' },
  {
    code: 'sl',
    name: 'Slovenščina',
    englishName: 'Slovenian',
    countryCode: 'SI',
  },
  { code: 'so', name: 'Soomaali', englishName: 'Somali', countryCode: 'SO' },
  { code: 'es', name: 'Español', englishName: 'Spanish', countryCode: 'ES' },
  {
    code: 'su',
    name: 'Basa Sunda',
    englishName: 'Sundanese',
    countryCode: 'ID',
  },
  { code: 'sw', name: 'Kiswahili', englishName: 'Swahili', countryCode: 'TZ' },
  { code: 'sv', name: 'Svenska', englishName: 'Swedish', countryCode: 'SE' },
  { code: 'tg', name: 'Тоҷикӣ', englishName: 'Tajik', countryCode: 'TJ' },
  { code: 'ta', name: 'தமிழ்', englishName: 'Tamil', countryCode: 'IN' },
  { code: 'tt', name: 'Татар', englishName: 'Tatar', countryCode: 'RU' },
  { code: 'te', name: 'తెలుగు', englishName: 'Telugu', countryCode: 'IN' },
  { code: 'th', name: 'ไทย', englishName: 'Thai', countryCode: 'TH' },
  { code: 'ti', name: 'ትግርኛ', englishName: 'Tigrinya', countryCode: 'ER' },
  { code: 'ts', name: 'Xitsonga', englishName: 'Tsonga', countryCode: 'ZA' },
  { code: 'tr', name: 'Türkçe', englishName: 'Turkish', countryCode: 'TR' },
  { code: 'tk', name: 'Türkmen', englishName: 'Turkmen', countryCode: 'TM' },
  { code: 'ak', name: 'Twi', englishName: 'Twi', countryCode: 'GH' },
  {
    code: 'uk',
    name: 'Українська',
    englishName: 'Ukrainian',
    countryCode: 'UA',
  },
  { code: 'ur', name: 'اردو', englishName: 'Urdu', countryCode: 'PK' },
  { code: 'ug', name: 'ئۇيغۇرچە', englishName: 'Uyghur', countryCode: 'CN' },
  { code: 'uz', name: 'Oʻzbek', englishName: 'Uzbek', countryCode: 'UZ' },
  {
    code: 'vi',
    name: 'Tiếng Việt',
    englishName: 'Vietnamese',
    countryCode: 'VN',
  },
  { code: 'cy', name: 'Cymraeg', englishName: 'Welsh', countryCode: 'GB' },
  { code: 'xh', name: 'isiXhosa', englishName: 'Xhosa', countryCode: 'ZA' },
  { code: 'yi', name: 'ייִדיש', englishName: 'Yiddish', countryCode: 'IL' },
  { code: 'yo', name: 'Yorùbá', englishName: 'Yoruba', countryCode: 'NG' },
  { code: 'zu', name: 'isiZulu', englishName: 'Zulu', countryCode: 'ZA' },
];

const GoogleTranslateContext = createContext({
  isLoaded: false,
  isDialogOpen: false,
  currentLanguage: null,
  languages: [],
  openDialog: () => {},
  closeDialog: () => {},
  translateTo: () => {},
  resetTranslation: () => {},
});

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

function setGoogTransCookie(value) {
  const maxAge = 60 * 60 * 24 * 365;
  const isSecure = window.location.protocol === 'https:';
  const sameSite = isSecure ? '; SameSite=None; Secure' : '; SameSite=Lax';
  const hostname = window.location.hostname;
  document.cookie = `googtrans=${value}; path=/; max-age=${maxAge}${sameSite}`;
  
  const rootDomain = hostname.split('.').slice(-2).join('.');
  if (rootDomain !== hostname && rootDomain.includes('.')) {
    document.cookie = `googtrans=${value}; path=/; domain=.${rootDomain}; max-age=${maxAge}${sameSite}`;
  }
}

function clearGoogTransCookie() {
  const hostname = window.location.hostname;
  const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = `googtrans=; ${expiry}; path=/`;
  document.cookie = `googtrans=; ${expiry}; path=/; domain=${hostname}`;
  const rootDomain = hostname.split('.').slice(-2).join('.');
  if (rootDomain !== hostname && rootDomain.includes('.')) {
    document.cookie = `googtrans=; ${expiry}; path=/; domain=.${rootDomain}`;
  }
}

export function GoogleTranslateProvider({ children, pageLanguage = 'en' }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(null);

  const languages = useMemo(() => GOOGLE_TRANSLATE_LANGUAGES, []);

  useEffect(() => {
    const googtrans = getCookie('googtrans');
    if (!googtrans) return;

    patchDomMethodsForGoogleTranslate();

    const isGoogleTranslateError = (error) => {
      const message = typeof error === 'string' ? error : error?.message || '';
      return GOOGLE_TRANSLATE_ERROR_PATTERNS.some(pattern => message.includes(pattern));
    };

    const handleError = (event) => {
      if (isGoogleTranslateError(event.error || event.message)) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };

    window.addEventListener('error', handleError, true);
    return () => window.removeEventListener('error', handleError, true);
  }, [pageLanguage]);

  useEffect(() => {
    const googtrans = getCookie('googtrans');
    if (googtrans) {
      const parts = googtrans.split('/').filter(Boolean);
      const targetLang = parts[parts.length - 1];
      if (targetLang && targetLang !== pageLanguage) {
        setCurrentLanguage(targetLang);
        setIsLoaded(true);
      }
    }
  }, [pageLanguage]);

  useEffect(() => {
    const googtrans = getCookie('googtrans');
    if (!googtrans) return;

    const parts = googtrans.split('/').filter(Boolean);
    const targetLang = parts[parts.length - 1];
    if (targetLang === pageLanguage) return;

    if (document.getElementById('google-translate-script')) return;

    if (!document.getElementById('google_translate_element')) {
      const container = document.createElement('div');
      container.id = 'google_translate_element';
      container.style.display = 'none';
      document.body.appendChild(container);
    }

    window.googleTranslateElementInit = () => {
      if (window.google?.translate?.TranslateElement) {
        new window.google.translate.TranslateElement(
          { pageLanguage, autoDisplay: false, multilanguagePage: true },
          'google_translate_element'
        );
        setIsLoaded(true);
      }
    };

    const script = document.createElement('script');
    script.id = 'google-translate-script';
    script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    document.body.appendChild(script);
  }, [pageLanguage]);

  const translateTo = useCallback((langCode) => {
    setIsDialogOpen(false);
    clearGoogTransCookie();
    setTimeout(() => {
      setGoogTransCookie(`/${pageLanguage}/${langCode}`);
      window.location.reload();
    }, 100);
  }, [pageLanguage]);

  const resetTranslation = useCallback(() => {
    setIsDialogOpen(false);
    clearGoogTransCookie();
    window.location.reload();
  }, []);

  return (
    <GoogleTranslateContext.Provider
      value={{
        isLoaded,
        isDialogOpen,
        currentLanguage,
        languages,
        openDialog: () => setIsDialogOpen(true),
        closeDialog: () => setIsDialogOpen(false),
        translateTo,
        resetTranslation,
      }}
    >
      {children}
    </GoogleTranslateContext.Provider>
  );
}

export const useGoogleTranslate = () => useContext(GoogleTranslateContext);
